import { useEffect } from "react";
import { Navigate, useLocation, useNavigate, useParams } from "react-router-dom";
import { useAuth } from "@/App";
import { scheduleScrollLockRelease } from "@/lib/scrollLock";
import {
  LayoutDashboard, Inbox, Users, HeartHandshake, Stethoscope, CalendarDays, Calendar,
  Target, CheckSquare, FileText, Wallet, FolderOpen, BookOpen, Settings,
  SlidersHorizontal, Shield, ShieldCheck
} from "lucide-react";

import Dashboard from "@/pages/Dashboard";
import InboxPage from "@/pages/InboxPage";
import ChildrenPage from "@/pages/ChildrenPage";
import CarersPage from "@/pages/CarersPage";
import ProvidersPage from "@/pages/ProvidersPage";
import AppointmentsPage from "@/pages/AppointmentsPage";
import GoalsPage from "@/pages/GoalsPage";
import ActivitiesPage from "@/pages/ActivitiesPage";
import SessionNotesPage from "@/pages/SessionNotesPage";
import BudgetsPage from "@/pages/BudgetsPage";
import DocumentsPage from "@/pages/DocumentsPage";
import MycyclopediaPage from "@/pages/MycyclopediaPage";
import SettingsPage from "@/pages/SettingsPage";
import AuditLogPage from "@/pages/AuditLogPage";
import ModerationPage from "@/pages/ModerationPage";

// Information architecture: top-level sections, each with ordered tabs.
// The first (visible) tab is the section's default landing tab.
export const SECTIONS = [
  {
    key: "overview", path: "/overview", label: "Overview", icon: LayoutDashboard,
    tabs: [
      { key: "summary", label: "Summary", icon: LayoutDashboard, component: Dashboard },
      { key: "inbox", label: "Inbox", icon: Inbox, component: InboxPage },
    ],
  },
  {
    key: "team", path: "/team", label: "Team", icon: Users,
    tabs: [
      { key: "children", label: "Children", icon: Users, component: ChildrenPage },
      { key: "carers", label: "Carers", icon: HeartHandshake, component: CarersPage },
      { key: "therapists", label: "Therapists", icon: Stethoscope, component: ProvidersPage },
    ],
  },
  {
    key: "planner", path: "/planner", label: "Planner", icon: CalendarDays,
    tabs: [
      { key: "calendar", label: "Calendar", icon: Calendar, component: AppointmentsPage },
      { key: "goals", label: "Goals", icon: Target, component: GoalsPage },
      { key: "activities", label: "Activities", icon: CheckSquare, component: ActivitiesPage },
      { key: "session-notes", label: "Session Notes", icon: FileText, component: SessionNotesPage },
      { key: "budgets", label: "Budgets", icon: Wallet, component: BudgetsPage },
    ],
  },
  {
    key: "resources", path: "/resources", label: "Resources", icon: FolderOpen,
    tabs: [
      { key: "documents", label: "Documents", icon: FolderOpen, component: DocumentsPage },
      { key: "library", label: "Library", icon: BookOpen, component: MycyclopediaPage },
    ],
  },
  // Community is a standalone section (no tabs) — rendered directly at /community.
  {
    key: "settings", path: "/settings", label: "Settings", icon: Settings,
    tabs: [
      { key: "general", label: "General", icon: SlidersHorizontal, component: SettingsPage },
      { key: "audit-log", label: "Audit Log", icon: Shield, component: AuditLogPage },
      { key: "moderation", label: "Moderation", icon: ShieldCheck, component: ModerationPage, visible: (user) => !!user?.is_moderator },
    ],
  },
];

// Old top-level URLs → new section/tab URLs (query string + hash are preserved).
export const LEGACY_REDIRECTS = {
  "/dashboard": "/overview/summary",
  "/inbox": "/overview/inbox",
  "/children": "/team/children",
  "/carers": "/team/carers",
  "/providers": "/team/therapists",
  "/therapists": "/team/therapists",
  "/appointments": "/planner/calendar",
  "/calendar": "/planner/calendar",
  "/goals": "/planner/goals",
  "/activities": "/planner/activities",
  "/session-notes": "/planner/session-notes",
  "/budgets": "/planner/budgets",
  "/documents": "/resources/documents",
  "/mycyclopedia": "/resources/library",
  "/library": "/resources/library",
  "/audit-log": "/settings/audit-log",
  "/moderation": "/settings/moderation",
};

export function LegacyRedirect({ to }) {
  const location = useLocation();
  return <Navigate to={{ pathname: to, search: location.search, hash: location.hash }} replace />;
}

export function visibleTabs(section, user) {
  return section.tabs.filter((t) => !t.visible || t.visible(user));
}

export default function SectionPage({ sectionKey }) {
  const { tab } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const section = SECTIONS.find((s) => s.key === sectionKey);
  const tabs = visibleTabs(section, user);
  const active = tabs.find((t) => t.key === tab);

  // Every time a tab lands: start at the top and make sure no stale overlay
  // scroll-lock (left over from a closed dialog/select) is freezing the page.
  useEffect(() => {
    if (!active) return undefined;
    window.scrollTo(0, 0);
    return scheduleScrollLockRelease();
  }, [active?.key, sectionKey]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!active) {
    return (
      <Navigate
        to={{ pathname: `${section.path}/${tabs[0].key}`, search: location.search, hash: location.hash }}
        replace
      />
    );
  }

  const ActiveComponent = active.component;

  return (
    <div className="space-y-6" data-testid={`section-${section.key}`}>
      <div
        role="tablist"
        aria-label={`${section.label} sections`}
        className="flex gap-1 p-1 bg-white rounded-full shadow-sm border border-border w-full sm:w-fit max-w-full overflow-x-auto no-scrollbar"
        data-testid={`section-tabs-${section.key}`}
      >
        {tabs.map((t) => {
          const isActive = t.key === active.key;
          const Icon = t.icon;
          return (
            <button
              key={t.key}
              type="button"
              role="tab"
              aria-selected={isActive}
              onClick={() => { if (!isActive) navigate(`${section.path}/${t.key}`); }}
              className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap flex-shrink-0 transition-all duration-200 ${
                isActive
                  ? "bg-primary text-primary-foreground shadow-md"
                  : "text-muted-foreground hover:bg-muted hover:text-foreground"
              }`}
              data-testid={`tab-${section.key}-${t.key}`}
            >
              <Icon className="h-4 w-4" />
              {t.label}
            </button>
          );
        })}
      </div>

      <div role="tabpanel" data-testid={`tabpanel-${section.key}-${active.key}`}>
        <ActiveComponent key={active.key} />
      </div>
    </div>
  );
}
